import type { DocsService } from '@affine/core/modules/doc';
import type { OrchestrationService } from '@affine/core/modules/orchestration';
import type {
  OrchestrationReference,
  OrchestrationRequest,
  OrchestrationResult,
} from '@affine/core/modules/orchestration/types';
import { nanoid } from 'nanoid';

import type { NotesIndexEntry } from './add-to-notes';
import { buildReferencePayload } from './build-quote-payload';

export interface SynthesizeNotesOptions {
  orchestrationService: OrchestrationService;
  docsService: DocsService;
  workspaceId: string;
  entries: ReadonlyArray<NotesIndexEntry>;
  /**
   * Called as streaming text-deltas arrive from the orchestration
   * backend. Passes the FULL accumulated text each time so the
   * caller can replace the rendered string without bookkeeping.
   */
  onPartial?: (accumulatedText: string) => void;
}

export interface SynthesizeNotesResult {
  synthesis: string;
  /** The same entries passed in — caller persists for provenance later. */
  entries: ReadonlyArray<NotesIndexEntry>;
  /** Citations the backend surfaced (echoed from OrchestrationResult). */
  citations: OrchestrationResult['citations'];
  /** Backend planId for telemetry / debugging. */
  planId: string;
}

const SYNTHESIS_PROMPT_TEMPLATE = (count: number) => `
You are helping a life-science researcher synthesise ${count} clippings they have collected from primary literature into a single atomic insight.

Below in the reference excerpt you will find ${count} numbered quotes, each prefixed with its source paper title. Each quote is identified as [Q1], [Q2], …, [Q${count}].

Your task:
1. Identify the dominant theme(s) that link these quotes.
2. Name any tensions or contradictions between them.
3. Surface up to 3 open questions the cluster raises but does not answer.
4. Suggest one concrete next direction (a paper, an experiment, or a hypothesis) the researcher could pursue.

Critical formatting rules:
- For every claim you make, cite the supporting quote(s) inline using the marker [Qn] where n matches the numbered quote.
- Use Markdown headings (##) for the four sections above.
- Be concise: 200–400 words total.
- Do NOT fabricate quotes or sources beyond what is in the reference excerpt.
- Preserve any [PMID:…], [DOI:…], [arXiv:…] citation tokens you see in the quotes.
`;

/**
 * Format the N selected entries into a single reference text the
 * orchestration backend will isolate inside `<reference_excerpt>`
 * tags. Each quote is numbered [Q1], [Q2]… so the model can cite
 * them inline and the frontend can post-process those tokens into
 * click-through chips back to the Browse page.
 *
 * Citation tokens are union'd across the entries — the backend's
 * existing citation-token plumbing then carries through to the
 * orchestration `citations` field for the result panel.
 */
function buildSynthesisReference(
  entries: ReadonlyArray<NotesIndexEntry>,
  docsService: DocsService
): OrchestrationReference | null {
  if (entries.length === 0) return null;

  const blocks: string[] = [];
  const sourceDocIds = new Set<string>();

  entries.forEach((entry, idx) => {
    const marker = `[Q${idx + 1}]`;
    let sourceLabel = 'unknown source';
    if (entry.sourceDocId) {
      sourceDocIds.add(entry.sourceDocId);
      try {
        const record = docsService.list.doc$(entry.sourceDocId).value;
        const title = record?.meta$.value.title?.trim();
        if (title) sourceLabel = title;
      } catch {
        /* fall back to default */
      }
    }
    blocks.push(`${marker} from "${sourceLabel}":\n"${entry.sourcePassage}"`);
  });

  // Run the aggregated text through the existing sanitiser so the
  // multi-quote reference gets the same prompt-injection defenses as
  // a single-quote "Ask Curiescious" reference. Citation tokens
  // embedded in the passages (e.g. "[PMID:12345]") are picked up by
  // the backend's reference-prompt handler from the wrapped text;
  // we don't have to plumb them as a separate field for v1.
  const raw = blocks.join('\n\n---\n\n');
  const sanitised = buildReferencePayload(raw, {
    maxChars: 16000, // larger cap — synthesis carries many quotes
    sourceDocId:
      sourceDocIds.size === 1 ? Array.from(sourceDocIds)[0] : undefined,
  });

  return sanitised
    ? {
        text: sanitised.text,
        sourceDocId: sanitised.sourceDocId,
        citationTokens: sanitised.citationTokens,
      }
    : null;
}

/**
 * Call the orchestration backend to synthesise N selected fleeting
 * notes into a draft atomic insight. Uses the non-streaming `execute`
 * path for v1 — streaming + intermediate UI is a follow-up.
 *
 * The returned synthesis text contains inline [Qn] markers that the
 * caller can render as click-through chips back to each fleeting
 * entry on the Browse page.
 */
export async function synthesizeNotes(
  opts: SynthesizeNotesOptions
): Promise<SynthesizeNotesResult> {
  const { orchestrationService, docsService, workspaceId, entries } = opts;
  if (entries.length === 0) {
    throw new Error('synthesizeNotes: no entries selected');
  }

  const reference = buildSynthesisReference(entries, docsService);
  if (!reference) {
    throw new Error('synthesizeNotes: failed to build reference payload');
  }

  const request: OrchestrationRequest = {
    query: SYNTHESIS_PROMPT_TEMPLATE(entries.length).trim(),
    mode: 'explore',
    workspaceId,
    sessionId: nanoid(),
    reference,
    options: { streaming: false },
  };

  // We use the non-streaming `execute()` path even when an onPartial
  // callback is supplied. Rationale: the production `executeWithStreaming`
  // subscribes to an SSE channel that the GraphQL resolver does NOT
  // currently push to (only the REST `/api/copilot/orchestration/execute`
  // endpoint streams text-deltas, and that path bypasses the GraphQL
  // mutation). Calling `executeWithStreaming` here would hang waiting
  // for events that never arrive. The non-streaming path returns the
  // full synthesis in one shot (8-30s) — the drawer shows a "Calling…"
  // state for the duration and then renders the full result.
  //
  // The onPartial callback is intentionally still on the API surface
  // so once the backend GraphQL→SSE wiring is fixed we can flip this
  // back to true streaming without any handler-side changes.
  void opts.onPartial;
  const result = await orchestrationService.execute(request);
  return {
    synthesis: result.synthesis,
    entries,
    citations: result.citations,
    planId: result.planId,
  };
}
